import { memo, useCallback, useRef, useState } from 'react';

import { Aperture, Code, Edit, MagnifyingGlass } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import { useAccountContext } from '~/providers/AccountProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type FlagValue } from '../types';
import { getBrowserName } from './OverviewTab';

type ToolsTabProps = {
  flagOverrides: Record<string, FlagValue>;
  apiRequests: Array<{
    id: string;
    name: string;
    method: string;
    status: number;
    duration: number;
    timestamp: Date;
  }>;
  isInspecting: boolean;
  setIsInspecting: (inspecting: boolean) => void;
};

/**
 * Tools tab providing various developer utilities.
 */
export const ToolsTab = memo(({ flagOverrides, apiRequests, isInspecting, setIsInspecting }: ToolsTabProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);

  const { data: account } = useAccountContext();
  const { currentWorkspace } = useCurrentWorkspace();

  const handleStartRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
      const rec = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus' });
      const chunks: BlobPart[] = [];

      rec.ondataavailable = (e) => e.data?.size && chunks.push(e.data);
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `air-screen-recording-${new Date().toISOString().slice(0, 19)}.webm`;
        a.click();
        setIsRecording(false);
        recorderRef.current = null;
      };

      rec.start(1000);
      recorderRef.current = rec;
      setIsRecording(true);

      const [videoTrack] = stream.getVideoTracks();
      videoTrack.addEventListener('ended', () => {
        if (rec.state === 'recording') rec.stop();
      });

      // Auto-stop after 5 minutes
      setTimeout(() => rec.state === 'recording' && rec.stop(), 5 * 60 * 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  }, []);

  const handleStopRecording = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.stop();
      rec.stream?.getTracks?.().forEach((t: MediaStreamTrack) => t.stop());
    }
  }, []);

  const handleCreateLinearIssue = useCallback(async () => {
    try {
      // Create the bug report content directly
      const reportText = `**Issue Description:**
[Please describe the issue you encountered]

**Steps to Reproduce:**
1.
2.
3.

**Expected Behavior:**
[What should have happened]

**Actual Behavior:**
[What actually happened]

**User Details:**
- Email: ${account?.email}
- User ID: ${account?.id}
- Workspace ID: ${currentWorkspace?.id}
- Workspace Name: ${currentWorkspace?.name}

**Environment:**
- URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}
- Browser: ${typeof navigator !== 'undefined' ? getBrowserName(navigator.userAgent) : 'N/A'}
- Timestamp: ${new Date().toISOString()}

**Feature Flag Overrides:**
\`\`\`json
${JSON.stringify(flagOverrides, null, 2)}
\`\`\`

**Recent API Requests:**
\`\`\`json
${JSON.stringify(
  apiRequests.slice(0, 15).map((req) => ({
    name: req.name,
    method: req.method,
    status: req.status,
    duration: req.duration,
    timestamp: req.timestamp.toISOString(),
  })),
  null,
  2,
)}
\`\`\``;

      // Create a pre-filled URL for Linear's issue creation using their supported deep linking
      const title = `Bug Report - ${new Date().toLocaleDateString()}`;
      // Use the generic linear.new URL - user can select their team in Linear
      const base = 'https://linear.new';

      // Create query parameters for Linear's supported fields
      const queryParams = new URLSearchParams({
        title: title,
        description: reportText,
        priority: 'medium',
        labels: 'bug,dev-panel,feature-flags',
      });

      const linearUrl = `${base}?${queryParams.toString()}`;

      // Open Linear with the deep link (this should pre-fill everything!)
      window.open(linearUrl, '_blank', 'noopener');
    } catch (error) {
      console.error(':x: Linear integration error:', error);
      alert(`:x: Failed to open Linear:\n\n${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [account, currentWorkspace, flagOverrides, apiRequests]);

  const handleDevToolsHelp = useCallback(() => {
    alert('To open Chrome DevTools: F12 / Ctrl+Shift+I / Cmd+Opt+I');
  }, []);

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="space-y-3 pb-4">
        <div>
          <div className="grid grid-cols-2 gap-4">
            <Button
              type="button"
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              prefix={<Aperture className="size-4" />}
            >
              {isRecording ? 'Stop Recording' : 'Record Screen'}
            </Button>

            <Button
              type="button"
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={() => setIsInspecting(!isInspecting)}
              prefix={<MagnifyingGlass className="size-4" />}
            >
              {isInspecting ? 'Stop Inspecting' : 'Element Inspector'}
            </Button>

            <Button
              type="button"
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={handleCreateLinearIssue}
              prefix={<Edit className="size-4" />}
            >
              Create Linear Issue
            </Button>

            <Button
              type="button"
              appearance="outline"
              color="grey"
              className="h-auto px-4 py-3 text-grey-12"
              onClick={handleDevToolsHelp}
              prefix={<Code className="size-4" />}
            >
              DevTools Help
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

ToolsTab.displayName = 'ToolsTab';
